import { type Account, accountKeys } from '@taler/api-client'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { isNotFound } from '@/app/not-found'
import { type AccountLabel, indexAccountLabels } from '../internal/account-labels.ts'

export interface UseAccountDetailOptions {
  readonly ledgerId: () => string | undefined
  readonly accountId: () => string
  readonly locale: () => 'en' | 'hu'
  readonly enabled: () => boolean
}

export interface UseAccountDetail {
  readonly account: ComputedRef<Account | undefined>
  /** Every account in the ledger, labelled - the path of this account and the other side of its entries. */
  readonly labels: ComputedRef<ReadonlyMap<string, AccountLabel>>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  /** The ledger has no account with this id: a 404, not a failure. */
  readonly notFound: ComputedRef<boolean>
  retry(): void
}

export function useAccountDetail(options: UseAccountDetailOptions): UseAccountDetail {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const accountQuery = useQuery({
    queryKey: computed(() => accountKeys.detail(options.ledgerId() ?? '', options.accountId())),
    queryFn: ({ signal }) => api.accounts.get(options.ledgerId()!, options.accountId(), signal),
    enabled: active,
    retry: false,
  })

  const treeQuery = useQuery({
    queryKey: computed(() => accountKeys.tree(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.accounts.tree(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  return {
    account: computed(() => accountQuery.data.value),
    labels: computed(() => indexAccountLabels(treeQuery.data.value ?? [], options.locale())),
    isLoading: computed(() => active.value && (accountQuery.isPending.value || treeQuery.isPending.value)),
    error: computed(() => accountQuery.error.value ?? treeQuery.error.value),
    retrying: retrying(accountQuery, treeQuery),
    notFound: computed(() => isNotFound(accountQuery.error.value)),
    retry: () => {
      void accountQuery.refetch()
      void treeQuery.refetch()
    },
  }
}
